import React, { useContext, useEffect, useState } from 'react';
import { cn, spacingClasses } from '@/lib/utils';
import { ButtonStyleType, SpacingType } from "@/types";
import { FooterCta } from './components/FooterCta';
import { StayConnect } from './components/StayConnect';
import { FooterCopy } from './components/FooterCopy';
import { FooterMenu } from './components/FooterMenu';
import { sectionVariants } from "@/types";
import { sectionBackgroundColors } from "@/lib/utils";
import { PageContext } from '@/components/builder';
import { useIsPreviewing } from '@builder.io/react';

interface FooterPropsType {   
  darkMode?: boolean;
  variant?: sectionVariants;
  spacing?: SpacingType;
  withCtaBanner?: boolean;
  ctaBanner?: {
    title?: string;
    subtitle?: string;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
  };
  withStayConnected?: boolean;
  stayConnected?: {
    title?: string;
    subtitle?: string;
    showLinkedIn?: boolean;
    linkedInLink?: string;
  };
  withFooterMenu?: boolean;
  footerMenu?: {
    title?: string;
    link?: string;
    menuItem?: {
      label?: string;
      link?: string;
    }[];
  }[];
  withCopyright?: boolean;
  policyLinks?: { label: string; link: string }[];
}

interface FooterProps extends FooterPropsType {
  attributes?: React.HTMLProps<HTMLElement>;
}

const DEFAULT_PROPS: Required<Omit<FooterPropsType, 'footerMenu' | 'policyLinks'>> = {
  darkMode: false,
  variant: "A",
  spacing: "xlarge",
  withCtaBanner: false,
  ctaBanner: {
    title: "",
    subtitle: "", 
    buttonText: "",
    buttonLink: "/",
    buttonStyle: "primary",
  },
  withStayConnected: false,
  stayConnected: {  
    title: "Stay Connected",
    subtitle: "Follow us on LinkedIn for the latest market insights, trends, and valuable resources.",
    showLinkedIn: false,
    linkedInLink: "/",
  },
  withFooterMenu: false,
  withCopyright: false,
};

// Utility function to resolve props based on preview mode
function resolveProps(
  contextProps: Partial<FooterPropsType> | undefined,
  directProps: Partial<FooterPropsType>,
  isPreviewing: boolean
): Required<Omit<FooterPropsType, 'footerMenu' | 'policyLinks'>> & { footerMenu?: FooterPropsType['footerMenu']; policyLinks?: FooterPropsType['policyLinks'] } {
  // Create a type-safe result object
  const result = {
    darkMode: false,  
    variant: "A",
    spacing: "xlarge",
    withCtaBanner: false,
    ctaBanner: {
      title: "",
      subtitle: "", 
      buttonText: "",
      buttonLink: "/",
      buttonStyle: "primary",
    },
    withStayConnected: false,
    stayConnected: {  
      title: "Stay Connected",
      subtitle: "Follow us on LinkedIn for the latest market insights, trends, and valuable resources.",
      showLinkedIn: false,
      linkedInLink: "/",
    },
    withFooterMenu: false,
    withCopyright: false,
  } as Required<Omit<FooterPropsType, 'footerMenu' | 'policyLinks'>>;
  
  // Helper to get value based on preview mode
  function getValue<T extends string | boolean | typeof result.ctaBanner | typeof result.stayConnected | FooterPropsType['footerMenu'] | FooterPropsType['policyLinks']>(
    contextValue: T | undefined,
    directValue: T | undefined,
    defaultValue: T
  ): T {
    return isPreviewing 
      ? (directValue ?? contextValue ?? defaultValue)
      : (contextValue ?? directValue ?? defaultValue);
  }

  // Handle simple props
  Object.entries(DEFAULT_PROPS).forEach(([key, defaultValue]) => {
    if (key !== 'ctaBanner' && key !== 'stayConnected') {
      const typedKey = key as keyof Omit<FooterPropsType, 'ctaBanner' | 'stayConnected' | 'footerMenu' | 'policyLinks'>;
      type ValueType = typeof DEFAULT_PROPS[typeof typedKey];
      (result as Record<typeof typedKey, ValueType>)[typedKey] = getValue<ValueType>(
        contextProps?.[typedKey],
        directProps[typedKey],
        defaultValue as ValueType
      );
    }
  });

  // Handle nested ctaBanner object
  Object.entries(DEFAULT_PROPS.ctaBanner).forEach(([key, defaultValue]) => {
    const typedKey = key as keyof typeof DEFAULT_PROPS.ctaBanner;
    type ValueType = typeof DEFAULT_PROPS.ctaBanner[typeof typedKey];
    (result.ctaBanner as Record<typeof typedKey, ValueType>)[typedKey] = getValue<ValueType>(
      contextProps?.ctaBanner?.[typedKey],
      directProps.ctaBanner?.[typedKey],
      defaultValue
    );
  });

  // Handle nested stayConnected object
  Object.entries(DEFAULT_PROPS.stayConnected).forEach(([key, defaultValue]) => {
    const typedKey = key as keyof typeof DEFAULT_PROPS.stayConnected;
    type ValueType = typeof DEFAULT_PROPS.stayConnected[typeof typedKey];
    (result.stayConnected as Record<typeof typedKey, ValueType>)[typedKey] = getValue<ValueType>(
      contextProps?.stayConnected?.[typedKey],
      directProps.stayConnected?.[typedKey],
      defaultValue
    );
  });

  // Handle footerMenu and policyLinks separately since they're optional
  const footerMenu = getValue<FooterPropsType['footerMenu'] | undefined>(
    contextProps?.footerMenu,
    directProps.footerMenu,
    undefined
  );

  const policyLinks = getValue<FooterPropsType['policyLinks'] | undefined>(
    contextProps?.policyLinks,
    directProps.policyLinks,
    undefined
  );

  return {
    ...result,
    footerMenu,
    policyLinks
  };
}

export function Footer(props: FooterProps) {
  const { data } = useContext(PageContext);
  const contextProps = (data as unknown as { footerProps?: Partial<FooterPropsType> })?.footerProps || ({} as Partial<FooterPropsType>);
  const isPreviewing = useIsPreviewing();
  const [hasMounted, setHasMounted] = useState(false);

  // Avoid hydration mismatch: preview detection can differ between SSR and browser (cookies/query params)
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Get effective values for each prop
  const effectiveProps = resolveProps(contextProps, props, hasMounted ? isPreviewing : false);

  const {
    darkMode,
    variant,
    spacing,
    withCtaBanner,
    ctaBanner,
    withStayConnected,
    stayConnected,
    withFooterMenu,
    footerMenu,
    withCopyright,
    policyLinks,
  } = effectiveProps;

  const { key } = props.attributes ?? {};

  const sectionClass = cn(
    darkMode ? "dark" : "light",
    spacingClasses[spacing],
    "m-0 overscroll-contain z-90 relative w-full transition-shadow duration-320 reserve:!bg-[#f4f0e8]",
    sectionBackgroundColors(darkMode, variant)
  );

  return (
    <>
      <footer 
        key={key}
        className={sectionClass}
      >
        {withCtaBanner && <FooterCta ctaBanner={ctaBanner} darkMode={darkMode} variant={variant} />}
        {withStayConnected && <StayConnect stayConnected={stayConnected} darkMode={darkMode} variant={variant} />}
        {withFooterMenu && <FooterMenu footerMenu={footerMenu} />}
        {withCopyright && <FooterCopy policyLinks={policyLinks} />}
      </footer>
      <div
        className={cn(
          darkMode ? "dark" : "light",
          sectionBackgroundColors(darkMode, variant)
        )}
        style={{ 
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: '-10',
          pointerEvents: 'none',
        }}
      />
    </>
  );
} 